import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Eye, EyeOff, Mail } from 'lucide-react';
import { toast } from 'sonner';

const MCareFlame = () => (
  <svg width="36" height="36" viewBox="0 0 34 34" fill="none">
    <rect width="34" height="34" rx="10" fill="#5A3B31"/>
    <path d="M17 6C17 6 10 12 10 18.5C10 22.09 13.13 25 17 25C20.87 25 24 22.09 24 18.5C24 12 17 6 17 6Z" fill="#F47B20"/>
    <path d="M17 14C17 14 14 17 14 19.5C14 21.16 15.34 22.5 17 22.5C18.66 22.5 20 21.16 20 19.5C20 17 17 14 17 14Z" fill="#FFB343"/>
    <circle cx="17" cy="20" r="2" fill="#FBF7F1" opacity="0.6"/>
  </svg>
);

export default function LoginPage() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('login');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState(false);
  const [form, setForm] = useState({
    email: '', password: '', first_name: '', last_name: '', phone: '', role: 'caregiver'
  });

  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(form.email, form.password);
      toast.success('Connexion réussie');
      const dest = user.role === 'super_admin' || user.role === 'admin_ops'
        ? '/admin' : user.role === 'partner' ? '/espace-partenaire' : '/espace-aidant';
      navigate(dest);
    } catch (err) {
      const msg = err?.message || err?.response?.data?.detail || 'Erreur de connexion';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    setLoading(true);
    try {
      const result = await register(form);
      if (result?.needsConfirmation) {
        setConfirmEmail(true);
        toast.info('Vérifiez votre email pour confirmer votre inscription');
      } else {
        toast.success('Inscription réussie');
        const dest = result.role === 'partner' ? '/espace-partenaire' : '/espace-aidant';
        navigate(dest);
      }
    } catch (err) {
      const msg = err?.message || err?.response?.data?.detail || "Erreur lors de l'inscription";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (confirmEmail) {
    return (
      <div className="min-h-screen flex items-center justify-center py-20 px-4 bg-background" data-testid="confirm-email-page">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-secondary/10 flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8 text-secondary" />
          </div>
          <h1 className="font-heading text-2xl font-bold mb-3">Vérifiez votre email</h1>
          <p className="text-muted-foreground mb-6">
            Un email de confirmation a été envoyé à <strong>{form.email}</strong>.
            Cliquez sur le lien pour activer votre compte, puis revenez vous connecter.
          </p>
          <Button onClick={() => { setConfirmEmail(false); setTab('login'); }} className="rounded-full" data-testid="back-to-login-btn">
            Retour à la connexion
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center py-20 px-4 bg-background" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2.5 mb-6">
            <MCareFlame />
            <div className="text-left">
              <span className="font-heading text-xl font-semibold text-primary block">M-CareLink</span>
              <span className="text-[10px] tracking-[0.15em] uppercase text-muted-foreground font-body">Séjours de répit adaptés</span>
            </div>
          </Link>
        </div>
        <div className="bg-white rounded-2xl p-8 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.05)] border border-border/50">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-2 w-full mb-6">
              <TabsTrigger value="login" data-testid="tab-login">Connexion</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register">Inscription</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="login-email">Email</Label>
                  <Input id="login-email" type="email" value={form.email} onChange={e => update('email', e.target.value)} placeholder="votre@email.com" required className="mt-1 rounded-xl" data-testid="login-email-input" />
                </div>
                <div>
                  <Label htmlFor="login-password">Mot de passe</Label>
                  <div className="relative mt-1">
                    <Input id="login-password" type={showPwd ? 'text' : 'password'} value={form.password} onChange={e => update('password', e.target.value)} placeholder="Votre mot de passe" required className="rounded-xl pr-10" data-testid="login-password-input" />
                    <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                      {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button type="submit" className="w-full rounded-full bg-primary text-primary-foreground py-5 font-semibold" disabled={loading} data-testid="login-submit-btn">
                  {loading ? 'Connexion...' : 'Se connecter'}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="reg-fname">Prénom</Label>
                    <Input id="reg-fname" value={form.first_name} onChange={e => update('first_name', e.target.value)} required className="mt-1 rounded-xl" data-testid="register-firstname-input" />
                  </div>
                  <div>
                    <Label htmlFor="reg-lname">Nom</Label>
                    <Input id="reg-lname" value={form.last_name} onChange={e => update('last_name', e.target.value)} required className="mt-1 rounded-xl" data-testid="register-lastname-input" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="reg-email">Email</Label>
                  <Input id="reg-email" type="email" value={form.email} onChange={e => update('email', e.target.value)} required className="mt-1 rounded-xl" data-testid="register-email-input" />
                </div>
                <div>
                  <Label htmlFor="reg-phone">Téléphone</Label>
                  <Input id="reg-phone" type="tel" value={form.phone} onChange={e => update('phone', e.target.value)} className="mt-1 rounded-xl" data-testid="register-phone-input" />
                </div>
                <div>
                  <Label htmlFor="reg-role">Je suis</Label>
                  <select id="reg-role" value={form.role} onChange={e => update('role', e.target.value)} className="mt-1 w-full h-10 px-3 rounded-xl border border-input bg-white text-sm" data-testid="register-role-select">
                    <option value="caregiver">Proche aidant</option>
                    <option value="partner">Partenaire professionnel</option>
                    <option value="b2b_contact">Organisme (mutuelle, CSE, collectivité)</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="reg-pwd">Mot de passe</Label>
                  <Input id="reg-pwd" type="password" value={form.password} onChange={e => update('password', e.target.value)} placeholder="Min. 6 caractères" required className="mt-1 rounded-xl" data-testid="register-password-input" />
                </div>
                <Button type="submit" className="w-full rounded-full bg-primary text-primary-foreground py-5 font-semibold" disabled={loading} data-testid="register-submit-btn">
                  {loading ? 'Inscription...' : "S'inscrire"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
